/**
 * HTML Sanitization utilities for Quill Editor
 * Used for cleaning pasted content and initializing editor content
 */

export interface SanitizeOptions {
  removeVarTags?: boolean;
  cleanAnchorAttributes?: boolean;
  removeContentEditableSpans?: boolean;
}

const DEFAULT_SANITIZE_OPTIONS: SanitizeOptions = {
  removeVarTags: true,
  cleanAnchorAttributes: true,
  removeContentEditableSpans: true,
};

/**
 * Sanitizes HTML content by removing unwanted tags and attributes
 * @param html - HTML string to sanitize
 * @param options - Sanitization options
 * @returns Sanitized HTML string
 */
export function sanitizeHTML(html: string, options: SanitizeOptions = {}): string {
  const opts = { ...DEFAULT_SANITIZE_OPTIONS, ...options };
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  sanitizeHTMLDocument(doc, opts);
  
  return doc.body.innerHTML;
}

/**
 * Sanitizes an HTML Document object
 * @param doc - Document object to sanitize
 * @param options - Sanitization options
 */
export function sanitizeHTMLDocument(doc: Document, options: SanitizeOptions = {}): void {
  const opts = { ...DEFAULT_SANITIZE_OPTIONS, ...options };

  if (opts.removeVarTags) {
    removeVarTags(doc);
  }

  if (opts.cleanAnchorAttributes) {
    cleanAnchorAttributes(doc);
  }

  if (opts.removeContentEditableSpans) {
    removeContentEditableSpans(doc);
  }
}

/**
 * Removes all <var> tags while preserving their content
 */
function removeVarTags(doc: Document): void {
  const varTags = Array.from(doc.querySelectorAll('var'));
  varTags.forEach(varTag => {
    const parent = varTag.parentNode;
    if (parent) {
      // Move all children out of the var tag
      while (varTag.firstChild) {
        parent.insertBefore(varTag.firstChild, varTag);
      }
      parent.removeChild(varTag);
    }
  });
}

/**
 * Cleans <a> tags by removing all attributes except href and target
 */
function cleanAnchorAttributes(doc: Document): void {
  const anchors = Array.from(doc.querySelectorAll('a'));
  anchors.forEach(anchor => {
    const href = anchor.getAttribute('href');
    const target = anchor.getAttribute('target');
    
    // Remove all attributes
    while (anchor.attributes.length > 0) {
      anchor.removeAttribute(anchor.attributes[0].name);
    }
    
    // Restore only href and target
    if (href) anchor.setAttribute('href', href);
    if (target) anchor.setAttribute('target', target);
  });
}

/**
 * Removes <span> tags that have contenteditable attribute while preserving their content
 */
function removeContentEditableSpans(doc: Document): void {
  const spans = Array.from(doc.querySelectorAll('span[contenteditable]'));
  spans.forEach(span => {
    const parent = span.parentNode;
    if (parent) {
      // Move all children out of the span tag
      while (span.firstChild) {
        parent.insertBefore(span.firstChild, span);
      }
      parent.removeChild(span);
    }
  });
}
